import axios from 'axios';
import { signOut } from '@/helpers/system-guard';

/**
 * The sign-in token's renewal. A token lasts JWT_TTL (24 hours by default); once it
 * expires the API answers 401 and the token is exchanged for a new one (POST /refresh,
 * possible for JWT_REFRESH_TTL, 14 days). Several requests failing at once share one
 * renewal; when it can't be renewed the user is signed out, told why.
 */

let pending = null;

/** A new token (stored and used from now on); rejects when it can't be renewed. */
export function refreshToken() {
  if (pending) return pending;
  const token = localStorage.getItem('jwt');
  if (!token) return Promise.reject(new Error('Not signed in'));

  // fetch, not axios: axios's own 401 handling (App.vue) would loop on this request.
  pending = fetch(`${process.env.VUE_APP_AUTHDOMAIN}/api/refresh`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
  })
    .then(async (r) => {
      const body = r.ok ? await r.json().catch(() => null) : null;
      const auth = body?.authorization;
      if (!auth?.token) throw new Error('The session could not be renewed');
      localStorage.setItem('jwt', auth.token);
      axios.defaults.headers.common.Authorization = `${auth.type || 'Bearer'} ${auth.token}`;
      return auth.token;
    })
    .finally(() => {
      // Kept a moment, so requests that failed together use the same new token.
      setTimeout(() => { pending = null; }, 1000);
    });
  return pending;
}

/** The session is over (it can't be renewed): to the sign-in page, which says so. */
export function sessionExpired() {
  if (!localStorage.getItem('jwt')) return;
  signOut({ type: 'expired', message: 'Your session has expired. Please sign in again.' });
}
